import { TFunction } from 'i18next'
import z from 'zod'

import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { popupFactory } from '@/factories'
import { ResidentParkingSpot, ResidentVehicle } from '@/models'
import * as S from '@/pages/parking-management/residents-parking/parking-modals/parking-modals.styled'
import { isEmpty } from '@/utils/general'

import { EMPTY_VEHICLE } from './parking-modals.constants'
import { VehicleFormData } from './parking-modals.types'

export const getEmptyVehicle = (): VehicleFormData => EMPTY_VEHICLE

export const mapApiVehicleToFormData = (vehicle: ResidentVehicle): VehicleFormData => ({
  color: vehicle.color ?? '',
  id: vehicle.id,
  licensePlate: vehicle.licensePlate,
  make: vehicle.make ?? '',
  model: vehicle.model ?? '',
  permitTag: vehicle.permitTag ?? '',
  residentId: null,
  // Note: Uneditable field. It is added to display source for selected vehicle.
  source: vehicle.source ?? '',
  year: vehicle.year ? vehicle.year.toString() : '',
})

export const getInitialResidentVehicles = (parkingSpot: ResidentParkingSpot): VehicleFormData[] => {
  const residentId = parkingSpot.residents?.[0]?.id ?? null
  if (parkingSpot.vehicles.length > 0) {
    return parkingSpot.vehicles.map((vehicle) => ({
      ...mapApiVehicleToFormData(vehicle),
      residentId,
    }))
  }
  return [{ ...getEmptyVehicle(), residentId }]
}

export const getInitialUnitVehicles = (parkingSpot: ResidentParkingSpot): VehicleFormData[] => {
  const residents = parkingSpot.residents ?? parkingSpot.unitResidents ?? []
  const vehicles = residents.flatMap((resident) => {
    const residentVehicles = resident.vehicles?.length
      ? resident.vehicles
      : parkingSpot.vehicles.filter((v) => v.clientId === resident.id)
    return residentVehicles.map((vehicle) => ({
      ...mapApiVehicleToFormData(vehicle),
      residentId: resident.id,
    }))
  })
  if (vehicles.length > 0) return vehicles
  const defaultResidentId = residents[0]?.id ?? null
  return [{ ...getEmptyVehicle(), residentId: defaultResidentId }]
}

export const buildVehicleDetails = (vehicle: VehicleFormData | undefined): string =>
  [
    vehicle?.licensePlate ?? '',
    [vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || undefined,
    vehicle?.color || undefined,
    vehicle?.year || undefined,
  ]
    .filter(Boolean)
    .join(' · ')

export const isEmptyVehicle = (vehicle: VehicleFormData) =>
  isEmpty(vehicle.licensePlate) &&
  isEmpty(vehicle.color) &&
  isEmpty(vehicle.make) &&
  isEmpty(vehicle.model) &&
  isEmpty(vehicle.permitTag) &&
  isEmpty(vehicle.year)

export const trimEmptyVehicles = (vehicles: VehicleFormData[]): VehicleFormData[] => {
  return vehicles.filter((vehicle) => !isEmptyVehicle(vehicle))
}

const occupiedLicensePlateErrorSchema = z.object({
  canAssociate: z.literal(false).optional(),
  errCode: z.literal(409),
  message: z.string(),
})

export const isOccupiedLicensePlateError = (
  error: unknown,
): error is z.infer<typeof occupiedLicensePlateErrorSchema> =>
  occupiedLicensePlateErrorSchema.safeParse(error).success

const vehicleApiErrorSchema = z.object({
  errCode: z.literal(409),
  message: z.string(),
})

export const isVehicleApiError = (error: unknown): boolean =>
  vehicleApiErrorSchema.safeParse(error).success

const duplicatedLicensePlateErrorResponseSchema = vehicleApiErrorSchema.extend({
  duplicatedLicensePlates: z.array(z.string()),
})

type DuplicatedLicensePlatesError = z.infer<typeof duplicatedLicensePlateErrorResponseSchema>

export const isDuplicatedLicensePlatesError = (
  error: unknown,
): error is DuplicatedLicensePlatesError => {
  return duplicatedLicensePlateErrorResponseSchema.safeParse(error).success
}

const canAssociateErrorResponseSchema = vehicleApiErrorSchema.extend({
  canAssociate: z.literal(true),
})

export type CanAssociateError = z.infer<typeof canAssociateErrorResponseSchema>

export const parseCanAssociateError = (error: unknown): CanAssociateError | null => {
  const result = canAssociateErrorResponseSchema.safeParse(error)
  return result.success ? result.data : null
}

export const openAssociateVehiclePopup = (
  vehicleDetails: string,
  t: TFunction<['parking-management']>,
  onConfirm: () => Promise<void>,
) => {
  popupFactory.open(
    ConfirmationPopup,
    {
      autoWidth: true,
      cancelText: t('parking-management:parkingModals.associateVehiclePopup.cancel'),
      children: (
        <S.AssociateVehiclePopupContent>
          <span>{t('parking-management:parkingModals.associateVehiclePopup.body')}</span>
          <S.AssociateVehicleDetails>{vehicleDetails}</S.AssociateVehicleDetails>
          <span>{t('parking-management:parkingModals.associateVehiclePopup.confirmPrompt')}</span>
        </S.AssociateVehiclePopupContent>
      ),
      confirmText: t('parking-management:parkingModals.associateVehiclePopup.confirm'),
      onConfirm,
      title: t('parking-management:parkingModals.associateVehiclePopup.title'),
      variant: 'warning',
    },
    { suspendCurrent: true },
  )
}
